import { Airframe } from "../types/airframe";
import { Component } from "../types/component";

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : null;

const toLowerText = (value: unknown): string =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const normalizeId = (value: unknown): string =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const looksLikeWarehouse = (value: unknown): boolean => {
  const text = toLowerText(value);
  if (!text) return false;
  return text.includes("warehouse") || text === "wh";
};

const looksLikeNonWarehouse = (value: unknown): boolean => {
  const text = toLowerText(value);
  if (!text) return false;
  return (
    text.includes("aircraft") ||
    text.includes("airframe") ||
    text.includes("installed") ||
    text.includes("repair") ||
    text.includes("shop")
  );
};

const isAttachedFromAirframes = (
  componentId: string | undefined,
  airframes: Airframe[]
): boolean => {
  const normalizedComponentId = normalizeId(componentId);
  if (!normalizedComponentId) return false;

  return airframes.some((airframe) =>
    (airframe.slots ?? []).some(
      (slot) => normalizeId(slot.installedComponent?.componentId) === normalizedComponentId
    )
  );
};

// Decide if a component should be visible in warehouse-driven UI lists.
export const isComponentInWarehouse = (
  component: Partial<Component>,
  airframes: Airframe[] = []
): boolean => {
  if (!component || component.isDeleted === true) return false;

  if (toLowerText(component.attachedToAirframe)) return false;
  if (isAttachedFromAirframes(component.id, airframes)) return false;

  const componentRecord = asRecord(component) ?? {};

  const directLocationType =
    componentRecord.locationType ?? asRecord(componentRecord.location)?.locationType;
  if (looksLikeWarehouse(directLocationType)) return true;
  if (looksLikeNonWarehouse(directLocationType)) return false;

  const directStateCode =
    componentRecord.stateCode ?? asRecord(componentRecord.state)?.stateCode;
  if (looksLikeWarehouse(directStateCode)) return true;
  if (looksLikeNonWarehouse(directStateCode)) return false;

  const componentStateHistories = Array.isArray(component.componentStateHistories)
    ? (component.componentStateHistories as unknown[])
    : [];
  const stateHistoryEntries = Array.isArray(componentRecord.stateHistoryEntries)
    ? (componentRecord.stateHistoryEntries as unknown[])
    : [];
  const histories = componentStateHistories.length > 0
    ? componentStateHistories
    : stateHistoryEntries;

  const activeHistory =
    histories.find((h) => {
      const historyRecord = asRecord(h);
      if (!historyRecord) return false;
      return !historyRecord.effectiveTo && !historyRecord.EffectiveTo;
    }) ?? histories[0];

  if (activeHistory) {
    const historyRecord = asRecord(activeHistory) ?? {};
    const historyLocationType =
      historyRecord.locationType ?? asRecord(historyRecord.location)?.locationType;
    const historyStateCode =
      historyRecord.stateCode ?? asRecord(historyRecord.state)?.stateCode;

    if (looksLikeWarehouse(historyLocationType) || looksLikeWarehouse(historyStateCode)) {
      return true;
    }

    if (looksLikeNonWarehouse(historyLocationType) || looksLikeNonWarehouse(historyStateCode)) {
      return false;
    }
  }

  // If the backend does not provide explicit state/location, treat unattached components as warehouse stock.
  return true;
};

export const filterWarehouseComponents = <T extends Partial<Component>>(
  components: T[],
  airframes: Airframe[] = []
): T[] => components.filter((c) => isComponentInWarehouse(c, airframes));

const getAttachedComponentIds = (airframes: Airframe[]): Set<string> => {
  const attachedComponentIds = new Set<string>();

  airframes.forEach((airframe) => {
    (airframe.slots ?? []).forEach((slot) => {
      const componentId = normalizeId(slot.installedComponent?.componentId);
      if (componentId) {
        attachedComponentIds.add(componentId);
      }
    });
  });

  return attachedComponentIds;
};

export const filterOrphanedInstalledComponents = <T extends Partial<Component>>(
  components: T[],
  airframes: Airframe[] = []
): T[] => {
  const attachedComponentIds = getAttachedComponentIds(airframes);
  if (attachedComponentIds.size === 0) {
    return components;
  }

  const installedStateIds = new Set<string>();

  components.forEach((component) => {
    const componentId = normalizeId(component.id);
    if (componentId && attachedComponentIds.has(componentId) && component.currentStateId) {
      installedStateIds.add(component.currentStateId);
    }
  });

  if (installedStateIds.size === 0) {
    return components;
  }

  return components.filter((component) => {
    const componentId = normalizeId(component.id);

    if (!componentId) {
      return false;
    }

    if (attachedComponentIds.has(componentId)) {
      return true;
    }

    if (!component.currentStateId) {
      return true;
    }

    return !installedStateIds.has(component.currentStateId);
  });
};
